/**
 * The identity document types MercadoPago takes per venue country, as the backend holds them
 * (`IdentityDocumentType` in meitre-backend-docker; BD-6 of
 * docs/plans/2026-10-21-mercadopago-approval-hardening.md): a static table, no SDK call. One
 * module feeds the selector, the submit validation and the card token, so the type the guest
 * picked is the type MercadoPago is told, everywhere.
 *
 * A country outside the table takes OTHER only, which is what every charge sent before the
 * document was collected. The backend drops a type its country does not take to OTHER and
 * reports it, so a stale table here costs a less useful payload, never a booking.
 */

export const OTHER_DOCUMENT_TYPE = 'Otro';

const BY_COUNTRY: Record<string, readonly string[]> = {
  AR: ['DNI', 'CI', 'LC', 'LE', OTHER_DOCUMENT_TYPE],
  BR: ['CPF', 'CNPJ', OTHER_DOCUMENT_TYPE],
  CL: ['RUT', OTHER_DOCUMENT_TYPE],
  CO: ['CC', 'CE', 'NIT', OTHER_DOCUMENT_TYPE],
  MX: ['RFC', 'CURP', OTHER_DOCUMENT_TYPE],
  PE: ['DNI', 'C.E', 'RUC', OTHER_DOCUMENT_TYPE],
  UY: ['CI', OTHER_DOCUMENT_TYPE],
};

/** The types a venue in `country` may offer, the country's usual one first; OTHER alone elsewhere. */
export function documentTypesFor(country: string | null | undefined): readonly string[] {
  return BY_COUNTRY[(country ?? '').toUpperCase()] ?? [OTHER_DOCUMENT_TYPE];
}

/** `previous` when the country offers it, else the country's first type. */
export function documentTypeFor(country: string | null | undefined, previous?: string | null): string {
  const types = documentTypesFor(country);
  return previous && types.includes(previous) ? previous : types[0];
}

/**
 * The number as MercadoPago validates it against the type: digits only under OTHER (what every
 * charge has sent), otherwise separators out and letters upper-cased (a Chilean RUT keeps its K,
 * a Peruvian C.E its letters). Mirrors the backend's `payerIdentification()`.
 */
export function normalizeDocument(type: string, number: string): string {
  return type === OTHER_DOCUMENT_TYPE
    ? number.replace(/\D/g, '')
    : number.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** Whether `number` is a plausible document of `type` in `country`; OTHER keeps the 4-character floor the field always had. */
export function isValidDocument(country: string | null | undefined, type: string, number: string): boolean {
  const value = normalizeDocument(type, number);
  if (value === '') return false;
  const rule = RULES[`${(country ?? '').toUpperCase()}:${type}`];
  if (rule === undefined) return value.length >= 4;
  return typeof rule === 'function' ? rule(value) : rule.test(value);
}

const RULES: Record<string, RegExp | ((value: string) => boolean)> = {
  'AR:DNI': /^\d{7,8}$/,
  'AR:CI': /^\d{6,9}$/,
  'AR:LC': /^\d{6,8}$/,
  'AR:LE': /^\d{6,8}$/,
  'BR:CPF': /^\d{11}$/,
  'BR:CNPJ': /^\d{14}$/,
  'CL:RUT': isValidRut,
  'CO:CC': /^\d{6,10}$/,
  'CO:CE': /^\d{6,10}$/,
  'CO:NIT': /^\d{9,10}$/,
  'MX:RFC': /^[A-Z0-9]{12,13}$/,
  'MX:CURP': /^[A-Z0-9]{18}$/,
  'PE:DNI': /^\d{8}$/,
  'PE:C.E': /^[A-Z0-9]{9,12}$/,
  'PE:RUC': /^\d{11}$/,
  'UY:CI': /^\d{7,8}$/,
};

/** A Chilean RUT: 7–8 digits plus the modulo-11 check digit (0–9 or K), separators already gone. */
function isValidRut(value: string): boolean {
  const match = /^(\d{7,8})([0-9K])$/.exec(value);
  if (!match) return false;
  const [, body, check] = match;
  let sum = 0;
  let factor = 2;
  for (let i = body.length - 1; i >= 0; i -= 1) {
    sum += Number(body[i]) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const expected = 11 - (sum % 11);
  const digit = expected === 11 ? '0' : expected === 10 ? 'K' : String(expected);
  return digit === check;
}

/** Peru's personal receipt carries the guest's DNI; an 8-digit one prefills the payer document (plan 2.3). */
export function peruPersonalDocumentForPrefill(country: string | null, billPersonalDocument: string): string | null {
  if (country !== 'PE') return null;
  const digits = billPersonalDocument.trim();
  return /^\d{8}$/.test(digits) ? digits : null;
}
